import { defineStore } from "pinia";
import type {
  AddressDto,
  AddToBasketDto,
  AOrder,
  AOrderLine,
  CarriageOptionDto,
  MultiShipDto,
  OrderAddressSaveDto,
  OrderLineAddressResultDto,
  OrderLineAddressSaveDto,
  OrderLinePersonResultDto,
  OrderLinePersonSaveDto,
  OrderLineQuantityResultDto,
  OrderSaveResultDto,
  PersonDto,
  ValidationDto,
  VoucherResultDto,
} from "../types";
import { AddressType } from "../constants/addresses";
import { useBasketState } from "../states/basketStates";
import { $basketResource, computed, getCmsBaseUrl, ref, useMultiShip, useNuxtApp } from "#imports";

/**
 * Basket store, contains methods and data for manipulating the basket
 */
export const useBasketStore = defineStore( "basket", () => {
  const basketResource = $basketResource();
  const basketState = useBasketState();
  const nuxtApp = useNuxtApp();

  const $personRef = $personResource();

  // Basket data
  const basketData = ref<AOrder | undefined>( basketState.value || undefined );
  const multiShipConfig = ref<MultiShipDto | undefined>( undefined );
  const carriageData = ref<CarriageOptionDto[] | undefined>( undefined );
  const validationData = ref<ValidationDto | undefined>( undefined );
  const toggleMultiShip = ref<boolean>( false );

  // Basket address data
  const invoiceAddress = ref<AddressDto | undefined>( undefined );
  const invoicePerson = ref<PersonDto | undefined>( undefined );
  const deliveryAddress = ref<AddressDto | undefined>( undefined );
  const deliveryPerson = ref<PersonDto | undefined>( undefined );

  // Shortcuts to commonly used data
  const basketLines = computed<AOrderLine[] | undefined>( () => basketData.value?.orderLines );
  const basketLinesExCarriage = computed<AOrderLine[] | undefined>( () => basketData.value?.orderLines?.filter( l => !l.isCarriage ) );
  const hasBasket = computed<boolean>( () => !!basketData.value );
  const basketEmpty = computed<boolean>( () => ( basketLines.value?.length || 0 ) === 0 );
  const basketId = computed<string | undefined>( () => basketData.value?.id );
  const isMultiShip = computed<boolean>( () => basketData.value?.isMultiShip || toggleMultiShip.value );
  const carriageRequired = computed<boolean>( () => basketData.value?.carriageRequired || false );
  const linkedToWebAccount = computed<boolean>( () => !!basketData.value?.webAccountId );

  // Total number of items in the basket
  const totalItems = computed<number>( () => {
    if ( !basketLines.value ) {
      return 0;
    }

    return basketLines.value.reduce( ( acc, prev ) => acc + ( prev.quantity || 0 ), 0 );
  } );

  // Total number of items in the basket excluding carriage
  const totalItemsExCarriage = computed<number>( () => {
    if ( !basketLinesExCarriage.value ) {
      return 0;
    }

    return basketLinesExCarriage.value.reduce( ( acc, prev ) => acc + ( prev.quantity || 0 ), 0 );
  } );

  // Order totals
  const totalShipping = computed<number>( () => basketData.value?.totalShipping || 0 );
  const totalVat = computed<number>( () => basketData.value?.totalVat || 0 );
  const subtotalBeforeDiscount = computed<number>( () => basketData.value?.subtotalBeforeDiscount || 0 );
  const totalIncVat = computed<number>( () => basketData.value?.totalIncVat || 0 );
  const totalExcVat = computed<number>( () => basketData.value?.totalExcVat || 0 );
  const totalExcShipping = computed<number>( () => ( totalIncVat.value - totalShipping.value ) );
  const totalDiscount = computed<number>( () => basketData.value?.totalDiscount || 0 );

  const totalExcVatExCarriage = computed<number>( () => {
    if ( !basketLinesExCarriage.value ) {
      return 0;
    }

    return basketLinesExCarriage.value.reduce( ( acc, prev ) => acc + ( prev.priceExcVat || 0 ), 0 );
  } );

  const totalIncVatExCarriage = computed<number>( () => {
    if ( !basketLinesExCarriage.value ) {
      return 0;
    }

    return basketLinesExCarriage.value.reduce( ( acc, prev ) => acc + ( prev.priceIncVat || 0 ), 0 );
  } );

  // Vouchers
  const vouchers = computed<Record<string, string> | undefined>( () => basketData.value?.vouchers );
  const voucherCodes = computed<string[]>( () => {
    if ( !vouchers.value ) {
      return [];
    }

    return Object.keys( vouchers.value );
  } );
  const voucherNames = computed<string[]>( () => {
    if ( !vouchers.value ) {
      return [];
    }

    return Object.values( vouchers.value );
  } );

  // Validation
  const isValid = computed<boolean>( () => {
    let valid = validationData.value?.isValid || false;

    if ( isMultiShip.value ) {
      valid = valid && multiShip.isMultiShipValid.value;
    }

    return valid;
  } );

  // Basket streaming via server side events
  let eventSource: EventSource;

  watch( basketId, ( newId, oldId ) => {
    connectToStream( newId, oldId );
  } );

  /**
   * Connect to the basket update stream
   */
  function connectToStream ( newId?: string, oldId?: string ) {
    // If we already have a stream, or we have an old basket ID then disconnect from it
    if ( eventSource?.readyState === 1 || oldId ) {
      eventSource.close();
    }

    const localNewId = newId ?? basketId.value;

    if ( !localNewId ) {
      // No new basket stream to connect to
      return;
    }

    // Connect to the basket stream
    const baseUrl = getCmsBaseUrl();
    const streamUrl = `${baseUrl}api/Basket/stream?newBasketId=${localNewId}&oldBasketId=${oldId}`;
    const maxReconnects = 10;
    let reconnects = 0;

    eventSource = new EventSource( streamUrl );

    eventSource.onmessage = ( event ) => {
      // When we receive basket data store it
      if ( !event?.data ) {
        return;
      }

      basketData.value = JSON.parse( event.data ) as AOrder;
    };

    eventSource.onerror = () => {
      // Something has gone wrong, try and reconnect 10 times
      if ( reconnects > maxReconnects ) {
        eventSource.close();
      }

      reconnects++;
    };
  }

  /**
   * Refresh the current basket from the server
   * @param dontPublish If supplied and true then we will just load the basket for ourselves,
   * we won't broadcast the data to anyone else
   */
  async function refreshBasket ( dontPublish?: boolean ) {
    basketData.value = await basketResource.getBasket( dontPublish );

    // Call custom Nuxt hook to signal we have refreshed the basket
    nuxtApp.callHook( "apollo:basket:refresh", basketData.value ).catch( () => {
    } );
  }

  /**
   * Check and refresh the basket only if the basket ID we have does not match the
   * basket ID the server says we should ave
   */
  async function checkBasket (): Promise<boolean> {
    const order = await basketResource.checkBasket( basketId.value );

    if ( !order ) {
      return false;
    }

    basketData.value = order;

    // Call custom Nuxt hook to signal we have refreshed the basket
    nuxtApp.callHook( "apollo:basket:refresh", basketData.value ).catch( () => {
    } );

    return true;
  }

  /**
   * Remove the client side basket
   */
  function removeClientBasket () {
    basketData.value = undefined;
    carriageData.value = undefined;
    validationData.value = undefined;
  }

  /**
   * Detach the basket from the session
   * (this will clear the basket and remove the cookie, any further
   * basket action will create a new basket as needed)
   */
  async function detachBasket (): Promise<boolean> {
    const detached = await basketResource.detachBasket();
    if ( detached ) {
      removeClientBasket();
    }
    return detached;
  }

  /**
   * Add multiple items to a basket
   * @param addDtos A list of items to add to the basket
   */
  async function bulkAddToBasket ( addDtos: AddToBasketDto[] ): Promise<OrderSaveResultDto> {
    const response = await basketResource.addToBasket( addDtos );
    if ( ( response?.addResults?.length || 0 ) > 0 ) {
      // Something was added so refresh the basket in the background
      await refreshBasket();

      // Call custom Nuxt hook to signal we have added to the basket
      nuxtApp.callHook( "apollo:basket:add", response ).catch( () => {
      } );
    }

    return response;
  }

  /**
   * Add a product to the basket
   * @param addDto Details on what to add
   */
  async function addToBasket ( addDto: AddToBasketDto ): Promise<OrderSaveResultDto> {
    return await bulkAddToBasket( [addDto] );
  }

  /**
   * User defined guard to check if an array is a list of order lines or a list of order
   * line IDs (strings)
   * @param items
   */
  function isOrderLines ( items: string[] | AOrderLine[] ): items is AOrderLine[] {
    return typeof items[0] !== "string";
  }

  /**
   * Remove items from an order in bulk
   * @param itemsToRemove The items to remove
   */
  async function bulkRemoveFromBasket ( itemsToRemove: string[] | AOrderLine[] ): Promise<OrderSaveResultDto | undefined> {
    if ( !hasBasket.value ) {
      // We have no basket so no point trying to remove anything
      return undefined;
    }

    let ids: string[] | undefined;

    if ( isOrderLines( itemsToRemove ) ) {
      ids = itemsToRemove.filter( ol => !!ol.id ).map( ol => ol.id || "" );
    } else {
      ids = itemsToRemove;
    }

    if ( !ids || ids.length === 0 ) {
      // No order line IDs supplied
      return undefined;
    }

    const response = await basketResource.removeFromBasket( ids );
    if ( response?.removeResults ) {
      // Item was removed so refresh the basket
      await refreshBasket();

      // Call custom Nuxt hook to signal we have removed from the basket
      nuxtApp.callHook( "apollo:basket:remove", response ).catch( () => {
      } );
    }

    return response;
  }

  /**
   * Remove a line from a basket
   * @param itemToRemove The line to remove or the ID of the line to remove
   */
  async function removeFromBasket ( itemToRemove: string | AOrderLine ): Promise<OrderSaveResultDto | undefined> {
    let items: string[] | AOrderLine[];

    // Build an array of the correct type of items to pass to the bulk function
    if ( typeof itemToRemove === "string" ) {
      items = [itemToRemove];
    } else {
      items = [itemToRemove];
    }

    return await bulkRemoveFromBasket( items );
  }

  /**
   * Update the quantity on a basket line
   * @param lineId The ID of the basket line to update
   * @param newQuantity The new quantity
   */
  async function updateQuantity ( lineId: string, newQuantity: number ): Promise<OrderLineQuantityResultDto | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    const response = await basketResource.updateQuantity( lineId, newQuantity );

    if ( response?.quantityUpdated ) {
      await refreshBasket();

      // Call custom Nuxt hook to signal we have updated a quantity basket
      nuxtApp.callHook( "apollo:basket:quantity", response ).catch( () => {
      } );
    }

    return response;
  }

  /**
   * Clear the basket
   */
  async function clearBasket (): Promise<boolean> {
    if ( !hasBasket.value ) {
      // We have no basket so no point trying to abandon it
      return false;
    }

    const cleared = await basketResource.clearBasket();

    if ( cleared && basketData.value ) {
      basketData.value.orderLines = [];

      // Refresh the order data
      await refreshBasket();
    }

    return cleared;
  }

  /**
   * Abandon the current basket
   */
  async function abandonBasket (): Promise<boolean> {
    if ( !hasBasket.value ) {
      // We have no basket so no point trying to abandon it
      return false;
    }

    const abandoned = await basketResource.abandonBasket();

    if ( abandoned ) {
      basketData.value = undefined;
    }

    return abandoned;
  }

  /**
   * Apply a voucher to the basket
   * @param voucherCode The voucher code to apply
   * @param removeExisting If true then existing vouchers will be removed
   */
  async function applyVoucher ( voucherCode: string, removeExisting: boolean ): Promise<VoucherResultDto | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    const response = await basketResource.applyVoucher( voucherCode, removeExisting );

    if ( response?.success ) {
      await refreshBasket();

      // Call custom Nuxt hook to signal we have applied a voucher a quantity basket
      nuxtApp.callHook( "apollo:basket:voucherApplied", voucherCode, response ).catch( () => {
      } );
    }

    return response;
  }

  /**
   * Remove a voucher from the basket
   * @param voucherCode The voucher to remove
   */
  async function removeVoucher ( voucherCode: string ): Promise<boolean> {
    if ( !hasBasket.value ) {
      return false;
    }

    const removed = await basketResource.removeVoucher( voucherCode );

    if ( removed ) {
      await refreshBasket();

      // Call custom Nuxt hook to signal we have removed a voucher a quantity basket
      nuxtApp.callHook( "apollo:basket:voucherRemoved", voucherCode ).catch( () => {
      } );
    }

    return removed;
  }

  /**
   * Set the address for the order header
   * @param addressSave Address and optionally person details to save
   */
  async function setAddress ( addressSave: OrderAddressSaveDto ): Promise<OrderAddressSaveDto | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    const response = await basketResource.setAddress( addressSave );

    if ( response && basketData.value ) {
      // We can update the local basket easily without doing an API call
      switch ( addressSave.type ) {
        case AddressType.Invoice: {
          basketData.value.invoiceOrgId = response.address?.orgId;
          basketData.value.invoiceAddressId = response.address?.id;
          basketData.value.invoicePersonId = response.person?.id;

          invoiceAddress.value = addressSave.address;
          invoicePerson.value = addressSave.person;
          break;
        }
        case AddressType.Delivery: {
          basketData.value.deliveryOrgId = response.address?.orgId;
          basketData.value.deliveryAddressId = response.address?.id;
          basketData.value.deliveryAddressId = response.person?.id;

          deliveryAddress.value = addressSave.address;
          deliveryPerson.value = addressSave.person;
          break;
        }
      }
    }

    return response;
  }

  /**
   * Get the order address
   * @param type The type of address
   */
  function getAddress ( type: AddressType ) {
    return type === AddressType.Delivery ? deliveryAddress.value : invoiceAddress.value;
  }

  /**
   * Get the order person and cache it
   * @param type The type of address
   */
  async function getPerson ( type: AddressType ) {
    // Get the person ID
    const personId = type === AddressType.Delivery
      ? basketData.value?.deliveryPersonId
      : basketData.value?.invoicePersonId;

    if ( !personId ) {
      return undefined;
    }

    let person = type === AddressType.Delivery
      ? deliveryPerson.value
      : invoicePerson.value;

    if ( person ) {
      // We have the data cached so return it
      return person;
    }

    // Get person from database
    person = await $personRef.getById( personId );

    if ( type === AddressType.Delivery ) {
      deliveryPerson.value = person;
    } else {
      invoicePerson.value = person;
    }

    return person;
  }

  /**
   * Refresh the carriage options
   */
  async function refreshCarriage () {
    carriageData.value = await basketResource.listCarriage();
    nuxtApp.callHook( "apollo:basket:carriage", carriageData.value ).catch( () => {
    } );
  }

  /**
   * Apply a carriage item to the basket
   * @param carriageId The carriage to apply
   */
  async function applyCarriage ( carriageId: number ): Promise<boolean> {
    const applied = await basketResource.applyCarriage( carriageId );

    if ( applied ) {
      // Call custom Nuxt hook to signal we have applied carriage to the basket
      nuxtApp.callHook( "apollo:basket:carriageApplied", carriageId ).catch( () => {
      } );

      // Refresh both basket and available carriage options
      await Promise.allSettled( [refreshCarriage(), refreshBasket()] );
    }

    return applied;
  }

  /**
   * Prepare the basket by refreshing all data, validating and loading configuration
   * @param inBasket If true then we are validating within the active basket so we need to do
   * some additional checks
   */
  async function prepareBasket ( inBasket: boolean ) {
    const data = await basketResource.fullBasket( inBasket );

    if ( data ) {
      basketData.value = data.order;
      carriageData.value = data.carriageOptions;
      validationData.value = data.validation;
      multiShipConfig.value = data.baseMultiShipConfig;

      if ( isMultiShip.value && multiShipConfig.value?.selectAddress ) {
        // Load full
        await prepareMultiShip();
      }

      // Call custom Nuxt hook to signal we have applied carriage to the basket
      nuxtApp.callHook( "apollo:basket:prepared" ).catch( () => {
      } );
    }
  }

  /**
   * Validate the current order
   * @param inBasket If true then we are validating within the active basket so we need to do
   * some additional checks
   */
  async function validate ( inBasket: boolean ) {
    validationData.value = await basketResource.validate( inBasket );

    // Call custom Nuxt hook to signal we have validated the basket
    nuxtApp.callHook( "apollo:basket:validate", validationData.value ).catch( () => {
    } );
  }

  /**
   * Clear delivery data from all lines in the current basket
   */
  async function clearDelivery (): Promise<boolean> {
    if ( !hasBasket.value ) {
      return false;
    }

    const response = await basketResource.clearDelivery();

    if ( response ) {
      toggleMultiShip.value = false;
      await refreshBasket();
    }

    return response;
  }

  /**
   * Set the default delivery address data across all order lines in the basket
   * @param orgId The Org ID to set
   * @param addressId The Address ID to set
   */
  async function setDefaultLineDelivery ( orgId: string, addressId: string ): Promise<boolean> {
    if ( !hasBasket.value ) {
      return false;
    }

    const response = await basketResource.setDefaultDelivery( orgId, addressId );

    if ( response ) {
      await refreshBasket();
    }

    return response;
  }

  /**
   * Update the delivery details for a specific order line
   * @param data Details about the address to apply
   */
  async function updateLineDelivery ( data: OrderLineAddressSaveDto ): Promise<OrderLineAddressResultDto | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    const response = await basketResource.updateLineDelivery( data );

    if ( ( response.lineIds?.length || 0 ) === 0 ) {
      return response;
    }

    if ( response.addresses && multiShipConfig.value ) {
      // Save the list of updated SOP addresses so that rest of the basket can use them
      multiShipConfig.value.addresses = response.addresses;
    }

    await refreshBasket();

    await nuxtApp.callHook( "apollo:basket:line:deliveryAddress", response );

    return response;
  }

  /**
   * Update the person against an order line
   * @param data Details of the person to save against an order line
   */
  async function updateLinePerson ( data: OrderLinePersonSaveDto ): Promise<OrderLinePersonResultDto | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    if ( !data.orderLineId ) {
      return undefined;
    }

    const response = await basketResource.updateLinePerson( data );

    if ( response.lineId && response.personId ) {
      await refreshBasket();
    }

    return response;
  }

  /**
   * Remove a person from an order line
   * @param line The line to remove the person from (ID or AOrderLine)
   */
  async function removeLinePerson ( line: string | AOrderLine ): Promise<string | undefined> {
    if ( !hasBasket.value ) {
      return undefined;
    }

    let lineId: string | undefined;
    if ( typeof line === "string" ) {
      lineId = line;
    } else {
      lineId = line?.id || undefined;
    }

    if ( !lineId ) {
      return undefined;
    }

    const response = await basketResource.removeLinePerson( lineId );

    if ( response ) {
      await refreshBasket();
    }

    return response || undefined;
  }

  /**
   * Load data needed to enable multi-ship
   */
  async function prepareMultiShip (): Promise<boolean> {
    if ( !hasBasket ) {
      return false;
    }

    const response = await basketResource.getMultiShipConfig();
    multiShipConfig.value = response;

    return !!response.selectAddress;
  }

  // Multi-ship
  const multiShip = useMultiShip( basketLines, multiShipConfig );

  return {
    connectToStream,
    prepareBasket,
    refreshBasket,
    checkBasket,
    removeClientBasket,
    detachBasket,
    bulkAddToBasket,
    addToBasket,
    removeFromBasket,
    updateQuantity,
    abandonBasket,
    clearBasket,
    applyVoucher,
    removeVoucher,
    setAddress,
    refreshCarriage,
    applyCarriage,
    validate,
    setDefaultLineDelivery,
    clearDelivery,
    updateLineDelivery,
    updateLinePerson,
    removeLinePerson,
    prepareMultiShip,
    carriageData,
    basketData,
    multiShipConfig,
    basketId,
    linkedToWebAccount,
    basketLines,
    basketLinesExCarriage,
    isMultiShip,
    carriageRequired,
    totalItems,
    totalExcVat,
    totalIncVat,
    totalExcShipping,
    totalShipping,
    totalDiscount,
    totalVat,
    totalExcVatExCarriage,
    totalIncVatExCarriage,
    totalItemsExCarriage,
    subtotalBeforeDiscount,
    hasBasket,
    validationData,
    isValid,
    basketEmpty,
    vouchers,
    voucherCodes,
    voucherNames,
    toggleMultiShip,
    getAddress,
    getPerson,
    invoicePerson,
    invoiceAddress,
    deliveryPerson,
    deliveryAddress,
    ...multiShip,
  };
} );
